/**
 * Section ID mapping between admin panel (Fiesta) and proposal website (Live Gallery)
 *
 * Admin panel sends standardized section IDs via postMessage to highlight and scroll
 * to corresponding sections in the proposal preview iframe.
 */

/**
 * Valid section IDs that can be sent from admin panel
 * @type {string[]}
 */
export const VALID_SECTION_IDS = [
  // Top-level sections
  'interface',
  'backdrop',
  'templates',
  'liveGallery',
  'pricing',
  'custom',
  'cta',

  // Modes (parent and subsections)
  'modes', // Parent section (fallback when no specific mode)
  'modes-photo',
  'modes-boomerang',
  'modes-video',
  'modes-gif',

  // Effects (parent and subsections)
  'effects', // Parent section (fallback when no specific effect)
  'effects-posetips',
  'effects-masks',
  'effects-glam',
  'effects-greenscreen',

  // Sharing subsections
  'sharing',
  'sharing-email',
  'sharing-sms',
  'sharing-print',
];

/**
 * Maps internal section IDs to admin panel section IDs
 *
 * Parent sections ('modes', 'effects', 'sharing') are automatically mapped to their
 * first enabled child section by useSectionHighlight hook.
 *
 * @type {Object.<string, string>}
 */
const SECTION_ID_MAP = {
  interfaceAndLed: 'interface',
  backdropSection: 'backdrop',
  templatesOverlays: 'templates',
  photos: 'modes-photo',
  boomerangs: 'modes-boomerang',
  videos: 'modes-video',
  gifs: 'modes-gif',
  poseTips: 'effects-posetips',
  masks: 'effects-masks',
  glam: 'effects-glam',
  greenScreen: 'effects-greenscreen',
  'sharing-general': 'sharing',
  email: 'sharing-email',
  sms: 'sharing-sms',
  printing: 'sharing-print',
  liveGallery: 'liveGallery',
  pricing: 'pricing',
  customSection: 'custom',
  callToAction: 'cta',
};

/**
 * Get admin panel section ID from internal section ID
 * @param {string} internalSectionId - Internal section ID (e.g., 'interfaceAndLed')
 * @returns {string} Admin panel section ID (e.g., 'interface')
 */
export const getSectionDataAttribute = (internalSectionId) => {
  return SECTION_ID_MAP[internalSectionId] || internalSectionId;
};

/**
 * Check if a section ID is valid
 * @param {string} sectionId - Section ID to validate
 * @returns {boolean} True if valid
 */
export const isValidSectionId = (sectionId) => {
  return VALID_SECTION_IDS.includes(sectionId);
};
